import { Dispatch, Suspense, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Input,
  Menu,
  MenuContent,
  MenuTrigger,
  Separator,
  Spinner,
  Text,
} from '@optiaxiom/react';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconBracketsCurly,
  IconCheck,
  IconCopy,
} from '@optimizely/axiom-icons';

import type { SpecializedAgentAction } from '@frontend/features/agents/hooks';
import * as agentStyles from '@frontend/styles/form.css';
import type { SpecializedAgent } from '@frontend/types/specialized-agent';

import { LazySandpackEditor } from '../../../shared/LazySandpackEditor';

import { styles } from './AgentFormOutput.css';
import { AgentOutputExamples } from './AgentOutputExamples';

type EditorToolbarProps = {
  title: string;
  isMaximized: boolean;
  isCopied: boolean;
  onCopy: () => void;
  onToggleMaximize: () => void;
};

function EditorToolbar({
  title,
  isMaximized,
  isCopied,
  onCopy,
  onToggleMaximize,
}: EditorToolbarProps) {
  return (
    <Flex
      flexDirection="row"
      alignItems="center"
      justifyContent="space-between"
      gap="8"
      p="12"
      bg="bg.secondary"
      borderB="1"
      borderColor="border.default"
      className={styles.lgRadiusTop}
    >
      <Flex flexDirection="row" gap="8">
        <IconBracketsCurly size={16} />
        <Text fontSize="sm" fontWeight="500">
          {title}
        </Text>
      </Flex>
      <Flex flexDirection="row" gap="8">
        <Flex cursor="pointer" onClick={onCopy} alignItems="center">
          {isCopied ? <IconCheck /> : <IconCopy />}
        </Flex>
        <Flex cursor="pointer" onClick={onToggleMaximize} alignItems="center">
          {isMaximized ? <IconArrowsMinimize /> : <IconArrowsMaximize />}
        </Flex>
      </Flex>
    </Flex>
  );
}

type SchemaEditorContentProps = {
  schemaInput: string;
  onSchemaChange: (content: string) => void;
  onFlushContent?: (getContent: () => string) => void;
};

function SchemaEditorContent({
  schemaInput,
  onSchemaChange,
  onFlushContent,
}: SchemaEditorContentProps) {
  return (
    <Suspense
      fallback={
        <Flex justifyContent="center" alignItems="center" h="full">
          <Spinner size="sm" />
        </Flex>
      }
    >
      <LazySandpackEditor
        content={schemaInput}
        onChange={onSchemaChange}
        isReadOnly={false}
        isPreviewMode={false}
        mimeType="application/json"
        lineNumberFontSize="14px"
        onFlushContent={onFlushContent}
      />
    </Suspense>
  );
}

type AgentFormOutputProps = {
  formData: SpecializedAgent;
  dispatch: Dispatch<SpecializedAgentAction>;
};

export function AgentFormOutput({ formData, dispatch }: AgentFormOutputProps) {
  // Create a dataTypes object for user-friendly labels
  const dataTypes = {
    string: 'Text',
    number: 'Number',
    boolean: 'Boolean',
    array: 'Array',
    json: 'JSON',
  } as const;

  // Helper to update nested output fields
  const updateOutputField = (outputUpdates: Partial<NonNullable<SpecializedAgent['output']>>) => {
    dispatch({ type: 'UPDATE_OUTPUT', value: outputUpdates });
  };

  // Local state for schema input and error
  const [schemaInput, setSchemaInput] = useState<string>(
    typeof formData.output?.schema === 'string'
      ? formData.output.schema
      : formData.output?.schema
        ? JSON.stringify(formData.output.schema, null, 2)
        : ''
  );
  const [schemaError, setSchemaError] = useState<string | null>(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const isLocalEditRef = useRef(false);
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const latestContentRef = useRef(schemaInput);

  const handleFlushContent = (getContent: () => string) => {
    const content = getContent();
    if (content !== latestContentRef.current) {
      handleSchemaChange(content, false);
    }
  };

  // Handler to copy schema to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(latestContentRef.current);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  const handleSchemaChange = (content: string, updateLocalState = true) => {
    isLocalEditRef.current = true;
    latestContentRef.current = content;
    if (updateLocalState) {
      setSchemaInput(content);
    }

    // Validate JSON on change
    if (content.trim() === '') {
      updateOutputField({ schema: '' });
      setSchemaError(null);
      return;
    }

    try {
      const parsed = JSON.parse(content);
      if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
        updateOutputField({ schema: parsed });
        setSchemaError(null);
      } else {
        setSchemaError('Schema must be a valid JSON dictionary/object.');
        updateOutputField({ schema: content });
      }
    } catch {
      setSchemaError('Schema must be a valid JSON dictionary/object.');
      updateOutputField({ schema: content });
    }
  };

  // Sync local state with formData changes (only for external updates)
  useEffect(() => {
    // Skip sync if the change came from local editing
    if (isLocalEditRef.current) {
      isLocalEditRef.current = false;
      return;
    }

    let newContent = '';
    if (typeof formData.output?.schema === 'string') {
      newContent = formData.output.schema;
    } else if (formData.output?.schema) {
      newContent = JSON.stringify(formData.output.schema, null, 2);
    }

    latestContentRef.current = newContent;
    setSchemaInput(newContent);
  }, [formData.output?.schema]);

  return (
    <Flex className={styles.container}>
      <Card bg="bg.secondary" px="8" py="4">
        <Disclosure w="full">
          <CardHeader>
            <DisclosureTrigger>
              <Text className={styles.fieldTitle}>Output</Text>
            </DisclosureTrigger>
          </CardHeader>
          <DisclosureContent>
            <Flex pt="24" pb="20" px="24">
              <Flex
                flexDirection="row"
                gap="16"
                pb="8"
                w="full"
                className={agentStyles.FormInputFieldMaxWidth}
              >
                <Field
                  label="Data Type"
                  info="Specifies the format of the output after this agent executes"
                  required
                >
                  <Menu
                    options={Object.entries(dataTypes).map(([type, label]) => ({
                      label,
                      execute: () => updateOutputField({ type }),
                      selected: formData.output?.type === type,
                    }))}
                  >
                    <MenuTrigger>
                      {dataTypes[formData.output?.type as keyof typeof dataTypes] || 'Text'}
                    </MenuTrigger>
                    <MenuContent />
                  </Menu>
                </Field>
                <Field label="Description" style={{ flex: 1 }}>
                  <Input
                    className={styles.outputField}
                    value={formData.output?.description || ''}
                    placeholder="Describe the output of your agent"
                    onChange={e => updateOutputField({ description: e.target.value })}
                  />
                </Field>
              </Flex>
              {formData.output?.type === 'json' && (
                <Field label="Output Schema" info="JSON schema for the output (optional)">
                  <Flex
                    flexDirection="column"
                    gap="4"
                    className={agentStyles.FormInputFieldMaxWidth}
                  >
                    <Box
                      h="384"
                      border="1"
                      borderColor="border.default"
                      rounded="lg"
                      style={{
                        ...(isMaximized && {
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          zIndex: 9999,
                          height: '100vh',
                          borderRadius: 0,
                          backgroundColor: 'var(--bg-default)',
                        }),
                      }}
                    >
                      <Flex flexDirection="column" h="full" gap="0">
                        <EditorToolbar
                          title={isMaximized ? 'JSON Schema' : 'JSON'}
                          isMaximized={isMaximized}
                          isCopied={isCopied}
                          onCopy={handleCopy}
                          onToggleMaximize={() => setIsMaximized(!isMaximized)}
                        />
                        <Box
                          ref={editorContainerRef}
                          flex="1"
                          h="full"
                          overflow="hidden"
                          className={styles.lgRadiusBottom}
                        >
                          <SchemaEditorContent
                            schemaInput={schemaInput}
                            onSchemaChange={handleSchemaChange}
                            onFlushContent={handleFlushContent}
                          />
                        </Box>
                      </Flex>
                    </Box>
                    {schemaError && (
                      <Text fontSize="sm" color="fg.error">
                        {schemaError}
                      </Text>
                    )}
                  </Flex>
                </Field>
              )}

              {formData.id && (
                <>
                  <Separator />
                  <AgentOutputExamples agentId={formData.id} agent={formData} dispatch={dispatch} />
                </>
              )}
            </Flex>
          </DisclosureContent>
        </Disclosure>
      </Card>
    </Flex>
  );
}
